/**
 * Inspector panel body — shows properties of the currently selected object.
 *
 * This replaces the old ObjectSidebar. It renders inside the unified panel
 * system (either docked or floating) and displays object info, type-specific
 * sub-panels (light, mannequin morphs, curve, edit mode), and action buttons.
 */

import { BookmarkPlus, Check, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  findCutterCollectionIdForObject,
  resolveCollectionCutterRef,
} from "../app/cutterCollections.ts";
import { getSingleSelectionTarget } from "../app/objectSelection.ts";
import { saveToCloudLibrary } from "../app/saveToLibrary.ts";
import { useStore } from "../app/store.ts";
import { useSelectedLeafObjectIds } from "../app/useSelectedLeafObjectIds.ts";
import type { EntityKind } from "../ecs/index.ts";
import { storeKeyToEntityId, useEntityRegistry } from "../ecs/index.ts";
import { Button } from "./Button.tsx";
import { showApplyGeneratorDialog } from "./ConfirmDialog.tsx";
import { EditModeIcon } from "./Icons.tsx";
import { SidebarCard } from "./SidebarCard.tsx";
import { SidebarButton, useTransientFeedback } from "./shared.tsx";
import { CurvePropertiesPanel } from "./sidebar/CurvePropertiesPanel.tsx";
import { CutterPanel } from "./sidebar/CutterPanel.tsx";
import { EditModeInfo } from "./sidebar/EditModePanel.tsx";
import { GeneratorPanel } from "./sidebar/GeneratorPanel.tsx";
import { MannequinMorphPanel } from "./sidebar/MannequinPanel.tsx";
import { LightSection } from "./sidebar/MaterialPanel.tsx";
import {
  COLOR_ACCENT,
  COLOR_SURFACE,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_PANEL_TITLE,
  FONT_SIZE_SECTION,
  FONT_SIZE_TINY,
  RADIUS_SM,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// ECS helpers
// ---------------------------------------------------------------------------

function entityKindLabel(kind: EntityKind): string {
  switch (kind) {
    case "mesh":
      return "Mesh";
    case "light":
      return "Light";
    case "camera":
      return "Camera";
    case "armature":
      return "Armature";
    case "empty":
      return "Object";
    case "mannequin":
      return "Mannequin";
    case "curve":
      return "Curve";
    case "image_plane":
      return "Image Plane";
  }
}

// ---------------------------------------------------------------------------
// Main inspector body — exported for use in panelRegistry.tsx
// ---------------------------------------------------------------------------

export function InspectorPanelBody() {
  const selectedObjects = useSelectedLeafObjectIds();
  const singleSelectionTarget = useStore((s) =>
    getSingleSelectionTarget(s.selectedObjectTargets),
  );
  const editMode = useStore((s) => s.editMode);
  const poseMode = useStore((s) => s.poseMode);
  const sculptMode = useStore((s) => s.sculptMode);
  const sketchMode = useStore((s) => s.sketchMode);
  const registry = useEntityRegistry();

  // Reactive flag: does the single-mesh selection own its own generator?
  // A stable boolean avoids returning a ref object that changes identity
  // on every param-drag frame; the GeneratorPanel subcomponent subscribes
  // to the object independently for its own redraws.
  const hasSingleMeshGenerator = useStore((s) => {
    const target = getSingleSelectionTarget(s.selectedObjectTargets);
    return (
      target?.kind === "sceneObject" &&
      s.sceneObjects.get(target.id)?.generator !== undefined
    );
  });

  // Reactive pull: when the selected collection owns a generator, the
  // inspector surfaces the GeneratorPanel scoped to that collection
  // instead of the multi-select panel. Split into primitive selectors
  // so each returns a stable value — a fresh `{id, name}` object on
  // every call would churn Zustand's equality and re-render in a loop.
  const generatorCollectionId = useStore((s) => {
    if (s.selectedObjectTargets.length !== 1) return null;
    const target = s.selectedObjectTargets[0];
    if (!target || target.kind !== "collection") return null;
    return s.collections.get(target.id)?.generatorRef ? target.id : null;
  });
  const generatorCollectionName = useStore((s) =>
    generatorCollectionId
      ? (s.collections.get(generatorCollectionId)?.name ?? null)
      : null,
  );

  const cutterCollectionId = useStore((s) => {
    const target = getSingleSelectionTarget(s.selectedObjectTargets);
    if (!target) return null;
    if (target.kind === "collection") {
      const collection = s.collections.get(target.id);
      if (!collection) return null;
      return resolveCollectionCutterRef(
        collection,
        s.sceneObjects,
        s.modifierOverrides,
      )
        ? target.id
        : null;
    }
    if (target.kind !== "sceneObject") return null;
    return findCutterCollectionIdForObject(
      s.collections,
      s.sceneObjects,
      s.modifierOverrides,
      target.id,
    );
  });
  const cutterCollectionName = useStore((s) =>
    cutterCollectionId
      ? (s.collections.get(cutterCollectionId)?.name ?? null)
      : null,
  );

  // Pose mode — inspector only
  if (poseMode) {
    const poseObjectId = poseMode.objectId;
    const entity = registry.get(storeKeyToEntityId(poseObjectId));
    const objectName = entity?.name ?? poseObjectId;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <ObjectInfoCard
          objectType="Mannequin"
          objectName={objectName}
          editMode={false}
          isSceneObject={true}
          isLight={false}
          isMannequin={true}
          objectId={poseObjectId}
        />
        <MannequinMorphPanel objectId={poseObjectId} />
      </div>
    );
  }

  // Sculpt mode — inspector only
  if (sculptMode) {
    const sculptObjectId = sculptMode.objectId;
    const entity = registry.get(storeKeyToEntityId(sculptObjectId));
    const objectName = entity?.name ?? sculptObjectId;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <ObjectInfoCard
          objectType={entity ? entityKindLabel(entity.kind) : "Mesh"}
          objectName={objectName}
          editMode={false}
          isSceneObject={true}
          isLight={false}
          isMannequin={false}
          objectId={sculptObjectId}
        />
      </div>
    );
  }

  // Sketch mode — inspector shows a generic header (sketch mode is no
  // longer bound to a specific scene object).
  if (sketchMode) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <ObjectInfoCard
          objectType="Sketch"
          objectName="Scene Sketch"
          editMode={false}
          isSceneObject={false}
          isLight={false}
          isMannequin={false}
          objectId=""
        />
      </div>
    );
  }

  // Generator-backed collection — selecting a Room (or any other
  // compound-generator collection) surfaces the GeneratorPanel scoped to
  // the collection. This takes precedence over the multi-select panel
  // because `selectCollection` populates `selectedObjects` with all
  // members, which would otherwise trigger the join flow.
  if (generatorCollectionId && generatorCollectionName) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <ObjectInfoCard
          objectType="Generator"
          objectName={generatorCollectionName}
          editMode={false}
          isSceneObject={false}
          isLight={false}
          isMannequin={false}
          objectId=""
        />
        <GeneratorPanel
          target={{ kind: "collection", id: generatorCollectionId }}
        />
      </div>
    );
  }

  if (
    singleSelectionTarget?.kind === "collection" &&
    cutterCollectionId &&
    cutterCollectionName
  ) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <ObjectInfoCard
          objectType="Cutter"
          objectName={cutterCollectionName}
          editMode={false}
          isSceneObject={false}
          isLight={false}
          isMannequin={false}
          objectId=""
        />
        <CutterPanel collectionId={cutterCollectionId} />
      </div>
    );
  }

  if (singleSelectionTarget?.kind === "collection") {
    const collection = useStore
      .getState()
      .collections.get(singleSelectionTarget.id);
    if (!collection) return <NoSelection />;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <ObjectInfoCard
          objectType="Collection"
          objectName={collection.name}
          editMode={false}
          isSceneObject={false}
          isLight={false}
          isMannequin={false}
          objectId=""
        />
      </div>
    );
  }

  if (singleSelectionTarget?.kind === "blendEntry") {
    const blendEntry = useStore
      .getState()
      .blendFiles.get(singleSelectionTarget.id);
    if (!blendEntry) return <NoSelection />;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <ObjectInfoCard
          objectType="Blend Group"
          objectName={blendEntry.fileName}
          editMode={false}
          isSceneObject={false}
          isLight={false}
          isMannequin={false}
          objectId=""
        />
      </div>
    );
  }

  // Multi-select panel — show Join button when 2+ scene mesh objects selected
  if (selectedObjects.size > 1) {
    let joinableCount = 0;
    for (const id of selectedObjects) {
      const ent = registry.get(storeKeyToEntityId(id));
      if (ent && ent.origin === "scene" && ent.kind === "mesh") joinableCount++;
    }
    if (joinableCount >= 2) {
      return <MultiSelectPanel count={joinableCount} />;
    }
    return <NoSelection />;
  }

  if (selectedObjects.size !== 1) return <NoSelection />;

  const selectedId = [...selectedObjects][0];
  const eid = storeKeyToEntityId(selectedId);
  const entity = registry.get(eid);

  if (!entity) return <NoSelection />;

  const objectName = entity.name;
  const objectType = entityKindLabel(entity.kind);
  const isSceneObject = entity.origin === "scene";
  const isLight = entity.kind === "light";
  const isMannequin = entity.kind === "mannequin";
  const isCurve = entity.kind === "curve";
  const sceneObjectId = isSceneObject ? selectedId : "";

  // ---- Panel registry ----
  const panels: {
    id: string;
    visible: boolean;
    render: () => React.ReactNode;
  }[] = [
    {
      id: "cutter",
      visible: isSceneObject && !editMode && cutterCollectionId !== null,
      render: () => <CutterPanel collectionId={cutterCollectionId!} />,
    },
    {
      id: "generator",
      // Object mode only — generator params aren't relevant mid-edit, and
      // the first destructive edit-mode op bakes the ref anyway. Compound
      // generators live on the *collection* and are handled by the
      // dedicated branch above; this row is only for single-mesh refs.
      visible: isSceneObject && !editMode && hasSingleMeshGenerator,
      render: () => (
        <GeneratorPanel target={{ kind: "object", id: sceneObjectId }} />
      ),
    },
    {
      id: "light",
      visible: isSceneObject && !editMode && isLight,
      render: () => <LightSection objectId={sceneObjectId} />,
    },
    {
      id: "mannequin_morphs",
      visible: isSceneObject && !editMode && isMannequin,
      render: () => <MannequinMorphPanel objectId={sceneObjectId} />,
    },
    {
      id: "curve_properties",
      visible: isSceneObject && !editMode && isCurve,
      render: () => <CurvePropertiesPanel objectId={sceneObjectId} />,
    },
    {
      id: "editmode",
      visible: !!editMode,
      render: () => <EditModeInfo />,
    },
  ];

  const visiblePanels = panels.filter((p) => p.visible);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Object info card — pinned at top */}
      <ObjectInfoCard
        objectType={objectType}
        objectName={objectName}
        editMode={!!editMode}
        isSceneObject={isSceneObject}
        isLight={isLight}
        isMannequin={isMannequin}
        objectId={sceneObjectId}
      />

      {/* Reorderable panels */}
      <ReorderablePanels panels={visiblePanels} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// No-selection placeholder
// ---------------------------------------------------------------------------

function NoSelection() {
  return (
    <div
      style={{
        fontSize: FONT_SIZE_BODY,
        color: COLOR_TEXT_DISABLED,
        padding: "4px 0",
      }}
    >
      No object selected
    </div>
  );
}

// ---------------------------------------------------------------------------
// Object info card — pinned at top, never reorderable
// ---------------------------------------------------------------------------

function ObjectInfoCard({
  objectType,
  objectName,
  editMode,
  isSceneObject,
  isLight,
  isMannequin,
  objectId,
}: {
  objectType: string;
  objectName: string;
  editMode: boolean;
  isSceneObject: boolean;
  isLight: boolean;
  isMannequin: boolean;
  objectId: string;
}) {
  const renameSceneObject = useStore((s) => s.renameSceneObject);
  const [isRenaming, setIsRenaming] = useState(false);
  const [editValue, setEditValue] = useState(objectName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(objectName);
    setIsRenaming(false);
  }, [objectName]);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const commitRename = useCallback(() => {
    setIsRenaming(false);
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== objectName) {
      renameSceneObject(objectId, trimmed);
    } else {
      setEditValue(objectName);
    }
  }, [editValue, objectName, objectId, renameSceneObject]);
  const beginRename = useCallback(() => {
    setEditValue(objectName);
    setIsRenaming(true);
  }, [objectName]);

  return (
    <SidebarCard style={{ padding: "12px 14px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZE_SECTION,
            color: COLOR_TEXT_FAINT,
            textTransform: "uppercase",
            letterSpacing: 0.8,
          }}
        >
          {objectType}
        </span>
        <span style={{ flex: 1 }} />
        {editMode && (
          <span
            style={{
              fontSize: FONT_SIZE_SECTION,
              color: COLOR_ACCENT,
              fontWeight: 500,
            }}
          >
            EDIT MODE
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: FONT_SIZE_PANEL_TITLE,
          fontWeight: 500,
          color: COLOR_TEXT_SECONDARY,
          marginBottom: 10,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {isSceneObject && !editMode ? (
          isRenaming ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (!(e.metaKey || e.ctrlKey)) e.stopPropagation();
                if (e.key === "Enter") commitRename();
                if (e.key === "Escape") {
                  setEditValue(objectName);
                  setIsRenaming(false);
                }
              }}
              style={{
                width: "100%",
                padding: "2px 4px",
                borderRadius: RADIUS_SM,
                border: `1px solid ${COLOR_ACCENT}`,
                background: COLOR_SURFACE,
                color: COLOR_TEXT_SECONDARY,
                fontSize: FONT_SIZE_PANEL_TITLE,
                fontWeight: 500,
                outline: "none",
              }}
            />
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onDoubleClick={beginRename}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "F2") beginRename();
              }}
              aria-label={`Rename ${objectName}`}
              title="Double-click to rename"
              style={{
                minHeight: "auto",
                padding: 0,
                color: "inherit",
                fontSize: FONT_SIZE_PANEL_TITLE,
                fontWeight: 500,
                lineHeight: "inherit",
                textAlign: "left",
                cursor: "text",
                justifyContent: "flex-start",
                maxWidth: "100%",
              }}
            >
              {objectName}
            </Button>
          )
        ) : (
          objectName
        )}
      </div>
      <div
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        {isSceneObject && !isLight && !isMannequin && (
          <EditModeButton objectId={objectId} />
        )}
        {isSceneObject && !editMode && !isLight && !isMannequin && (
          <SaveToLibraryButton objectId={objectId} />
        )}
        {isMannequin && !editMode && <ResetPoseButton objectId={objectId} />}
        {isSceneObject && !editMode && <DeleteButton objectId={objectId} />}
      </div>
    </SidebarCard>
  );
}

// ---------------------------------------------------------------------------
// Multi-select panel
// ---------------------------------------------------------------------------

function MultiSelectPanel({ count }: { count: number }) {
  const joinObjects = useStore((s) => s.joinObjects);
  const mergeObjects = useStore((s) => s.mergeObjects);

  return (
    <SidebarCard style={{ padding: "12px 14px" }}>
      <div
        style={{
          fontSize: FONT_SIZE_SECTION,
          color: COLOR_TEXT_FAINT,
          textTransform: "uppercase",
          letterSpacing: 0.8,
          marginBottom: 8,
        }}
      >
        {count} Objects Selected
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <Button onClick={() => joinObjects()} variant="secondary" fullWidth>
          Join Objects
          <span
            style={{
              fontSize: FONT_SIZE_TINY,
              color: COLOR_TEXT_FAINT,
              marginLeft: 4,
            }}
          >
            {"\u2318"}J
          </span>
        </Button>
        <Button onClick={() => mergeObjects()} variant="secondary" fullWidth>
          Merge Objects
          <span
            style={{
              fontSize: FONT_SIZE_TINY,
              color: COLOR_TEXT_FAINT,
              marginLeft: 4,
            }}
          >
            {"\u2318"}
            {"\u2325"}J
          </span>
        </Button>
      </div>
    </SidebarCard>
  );
}

// ---------------------------------------------------------------------------
// Panel ordering — persisted in localStorage
// ---------------------------------------------------------------------------

const PANEL_ORDER_KEY = "poseref-panel-order";
const DEFAULT_ORDER = [
  "cutter",
  "generator",
  "light",
  "mannequin_morphs",
  "curve_properties",
  "editmode",
];

function enforcePanelOrderConstraints(order: string[]): string[] {
  const next = [...order];
  const cutterIndex = next.indexOf("cutter");
  const generatorIndex = next.indexOf("generator");
  if (cutterIndex >= 0 && generatorIndex >= 0 && cutterIndex > generatorIndex) {
    next.splice(cutterIndex, 1);
    next.splice(generatorIndex, 0, "cutter");
  }
  return next;
}

function readPanelOrder(): string[] {
  try {
    const raw = localStorage.getItem(PANEL_ORDER_KEY);
    return raw ? enforcePanelOrderConstraints(JSON.parse(raw)) : DEFAULT_ORDER;
  } catch {
    return DEFAULT_ORDER;
  }
}

function writePanelOrder(order: string[]) {
  localStorage.setItem(PANEL_ORDER_KEY, JSON.stringify(order));
}

// ---------------------------------------------------------------------------
// ReorderablePanels — hold-and-drag to reorder
// ---------------------------------------------------------------------------

interface DragSnapshot {
  dragId: string;
  startY: number;
  currentY: number;
  origIdx: number;
  order: string[];
  centers: Map<string, number>;
  heights: Map<string, number>;
}

function ReorderablePanels({
  panels,
}: {
  panels: { id: string; render: () => React.ReactNode }[];
}) {
  const [orderedIds, setOrderedIds] = useState(() => {
    const saved = readPanelOrder();
    const panelIds = new Set(panels.map((p) => p.id));
    const ordered = saved.filter((id) => panelIds.has(id));
    for (const p of panels) {
      if (!ordered.includes(p.id)) ordered.push(p.id);
    }
    return enforcePanelOrderConstraints(ordered);
  });

  const panelIds = new Set(panels.map((p) => p.id));
  const reconciledIds = useMemo(() => {
    const kept = orderedIds.filter((id) => panelIds.has(id));
    for (const p of panels) {
      if (!kept.includes(p.id)) kept.push(p.id);
    }
    return enforcePanelOrderConstraints(kept);
  }, [orderedIds, panelIds.has, panels]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      reconciledIds.length !== orderedIds.length ||
      reconciledIds.some((id, i) => id !== orderedIds[i])
    ) {
      setOrderedIds(reconciledIds);
    }
  }, [reconciledIds, orderedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const panelMap = new Map(panels.map((p) => [p.id, p]));
  const sorted = reconciledIds
    .map((id) => panelMap.get(id))
    .filter((p): p is { id: string; render: () => React.ReactNode } => !!p);

  const [drag, setDrag] = useState<DragSnapshot | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<{ id: string; y: number } | null>(null);

  const snapshot = useCallback((): {
    centers: Map<string, number>;
    heights: Map<string, number>;
  } => {
    const centers = new Map<string, number>();
    const heights = new Map<string, number>();
    if (!containerRef.current) return { centers, heights };
    for (const child of containerRef.current.children) {
      const el = child as HTMLElement;
      const id = el.dataset.panelId;
      if (!id) continue;
      const rect = el.getBoundingClientRect();
      centers.set(id, rect.top + rect.height / 2);
      heights.set(id, rect.height + 8);
    }
    return { centers, heights };
  }, []);

  const computeTargetIdx = useCallback((d: DragSnapshot): number => {
    const delta = d.currentY - d.startY;
    const dragCenter = (d.centers.get(d.dragId) ?? 0) + delta;
    const others = d.order.filter((id) => id !== d.dragId);
    let target = 0;
    for (let i = 0; i < others.length; i++) {
      if (dragCenter > (d.centers.get(others[i]) ?? 0)) target = i + 1;
    }
    return target;
  }, []);

  const buildFinalOrder = useCallback(
    (d: DragSnapshot): string[] => {
      const others = d.order.filter((id) => id !== d.dragId);
      const target = computeTargetIdx(d);
      others.splice(target, 0, d.dragId);
      return others;
    },
    [computeTargetIdx],
  );

  const onPointerDown = useCallback(
    (id: string, e: React.PointerEvent) => {
      const target = e.target as HTMLElement;
      const isHeader = target.closest("[data-panel-header]") != null;
      if (!isHeader) return;

      pending.current = { id, y: e.clientY };
      holdTimer.current = setTimeout(() => {
        if (!pending.current) return;
        const { centers, heights } = snapshot();
        const order = sorted.map((p) => p.id);
        setDrag({
          dragId: pending.current.id,
          startY: pending.current.y,
          currentY: pending.current.y,
          origIdx: order.indexOf(pending.current.id),
          order,
          centers,
          heights,
        });
        pending.current = null;
      }, 250);
    },
    [sorted, snapshot],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (pending.current && Math.abs(e.clientY - pending.current.y) > 5) {
        if (holdTimer.current) clearTimeout(holdTimer.current);
        pending.current = null;
      }
      if (drag) {
        setDrag((prev) => (prev ? { ...prev, currentY: e.clientY } : null));
      }
    },
    [drag],
  );

  const onPointerUp = useCallback(() => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    pending.current = null;
    if (!drag) return;

    const finalVisible = enforcePanelOrderConstraints(buildFinalOrder(drag));

    const saved = readPanelOrder();
    const visibleSet = new Set(finalVisible);
    const merged: string[] = [];
    let vi = 0;
    for (const id of saved) {
      if (visibleSet.has(id)) merged.push(finalVisible[vi++]);
      else merged.push(id);
    }
    for (const id of finalVisible) {
      if (!merged.includes(id)) merged.push(id);
    }

    writePanelOrder(merged);
    setOrderedIds(finalVisible);
    setDrag(null);
  }, [drag, buildFinalOrder]);

  const panelStyle = useCallback(
    (id: string): React.CSSProperties => {
      if (!drag) return {};
      const { dragId, startY, currentY, origIdx, order, heights } = drag;
      const dragHeight = heights.get(dragId) ?? 50;

      if (id === dragId) {
        return {
          transform: `translateY(${currentY - startY}px)`,
          zIndex: 10,
          opacity: 0.92,
          boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          transition: "box-shadow 0.15s, opacity 0.15s",
          pointerEvents: "none",
        };
      }

      const others = order.filter((pid) => pid !== dragId);
      const targetIdx = computeTargetIdx(drag);
      const myIdxAmongOthers = others.indexOf(id);
      const origIdxAmongOthers = Math.min(origIdx, others.length);

      let shift = 0;
      if (targetIdx > origIdxAmongOthers) {
        if (
          myIdxAmongOthers >= origIdxAmongOthers &&
          myIdxAmongOthers < targetIdx
        ) {
          shift = -dragHeight;
        }
      } else if (targetIdx < origIdxAmongOthers) {
        if (
          myIdxAmongOthers >= targetIdx &&
          myIdxAmongOthers < origIdxAmongOthers
        ) {
          shift = dragHeight;
        }
      }

      return {
        transform: shift ? `translateY(${shift}px)` : "none",
        transition: "transform 0.2s ease",
      };
    },
    [drag, computeTargetIdx],
  );

  return (
    <div
      ref={containerRef}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        touchAction: drag ? "none" : "auto",
      }}
    >
      {sorted.map((panel) => (
        <div
          key={panel.id}
          data-panel-id={panel.id}
          onPointerDown={(e) => onPointerDown(panel.id, e)}
          style={{
            position: "relative",
            cursor: drag?.dragId === panel.id ? "grabbing" : undefined,
            ...panelStyle(panel.id),
          }}
        >
          {panel.render()}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action buttons
// ---------------------------------------------------------------------------

function EditModeButton({ objectId }: { objectId: string }) {
  const editMode = useStore((s) => s.editMode);
  const enterEditMode = useStore((s) => s.enterEditMode);
  const exitEditMode = useStore((s) => s.exitEditMode);
  const applyGenerator = useStore((s) => s.applyGenerator);
  // "single" if the object itself has a generator ref, "group" if it belongs to
  // a generator-backed collection, null otherwise.
  const generatorKind = useStore((s) => {
    const object = s.sceneObjects.get(objectId);
    if (!object) return null as null;
    if (object.generator) return "single" as const;
    for (const collection of s.collections.values()) {
      if (collection.generatorRef && collection.objectIds.has(objectId))
        return "group" as const;
    }
    return null as null;
  });
  // Collection ID for group generators (null for single-mesh or no generator).
  const generatorCollectionId = useStore((s) => {
    for (const [collId, collection] of s.collections) {
      if (collection.generatorRef && collection.objectIds.has(objectId))
        return collId;
    }
    return null;
  });
  const isEditing = editMode?.objectId === objectId;

  const handleClick = useCallback(async () => {
    if (isEditing) {
      exitEditMode();
      return;
    }
    if (!generatorKind) {
      enterEditMode(objectId);
      return;
    }
    const isGroup = generatorKind === "group";
    const confirmed = await showApplyGeneratorDialog(isGroup);
    if (!confirmed) return;
    const target = isGroup
      ? { kind: "collection" as const, id: generatorCollectionId! }
      : { kind: "object" as const, id: objectId };
    applyGenerator(target);
    // For single-mesh generators, also enter edit mode after applying.
    if (!isGroup) enterEditMode(objectId);
  }, [
    isEditing,
    exitEditMode,
    generatorKind,
    generatorCollectionId,
    enterEditMode,
    objectId,
    applyGenerator,
  ]);

  return (
    <SidebarButton
      icon={<EditModeIcon />}
      label={isEditing ? "Exit Edit" : "Edit Mode"}
      active={isEditing}
      onClick={handleClick}
      shortcut="Tab"
    />
  );
}

function SaveToLibraryButton({ objectId }: { objectId: string }) {
  const addToLibrary = useStore((s) => s.addToLibrary);
  const [saved, fireSaved] = useTransientFeedback();
  const [saving, setSaving] = useState(false);

  const handleClick = useCallback(async () => {
    addToLibrary(objectId);

    setSaving(true);
    try {
      await saveToCloudLibrary([objectId]);
    } catch {
      // Cloud save is best-effort — local save already succeeded
    }
    setSaving(false);
    fireSaved();
  }, [addToLibrary, objectId, fireSaved]);

  return (
    <SidebarButton
      icon={saved ? <Check size={13} /> : <BookmarkPlus size={13} />}
      label={saving ? "Saving…" : saved ? "Saved!" : "To Library"}
      active={saved}
      onClick={handleClick}
    />
  );
}

function DeleteButton({ objectId }: { objectId: string }) {
  const removeSceneObject = useStore((s) => s.removeSceneObject);

  return (
    <SidebarButton
      icon={<Trash2 size={13} />}
      label="Delete"
      active={false}
      onClick={() => removeSceneObject(objectId)}
      danger
    />
  );
}

function ResetPoseButton({ objectId }: { objectId: string }) {
  const resetPose = useStore((s) => s.resetPose);

  return (
    <SidebarButton
      icon={<RefreshCw size={13} />}
      label="Reset Pose"
      active={false}
      onClick={() => resetPose(objectId)}
    />
  );
}
